import { useState, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, X, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { useBrowserDetection } from "@/hooks/use-browser-detection";
import type { Participant, JamNight } from "@shared/schema";

const formSchema = z.object({
  participants: z.array(z.object({
    participantId: z.number().optional(),
    name: z.string().min(1, "Participant name is required"),
  })).min(1, "At least one participant is required"),
  notes: z.string().optional(),
  priority: z.boolean().optional().default(false),
  jamNightId: z.number(),
});

interface AddGenericEventParticipantModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jamNightId: number;
}

export default function AddGenericEventParticipantModal({ open, onOpenChange, jamNightId }: AddGenericEventParticipantModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [activeInputIndex, setActiveInputIndex] = useState<number>(-1);

  // Detect Chrome browsers for different transparency treatment
  const { isChrome, isMobileSafari } = useBrowserDetection();

  // Fetch jam night details to get organisation ID
  const { data: jamNight } = useQuery({
    queryKey: [`/api/events/${jamNightId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/events/${jamNightId}`);
      return response.json();
    },
    enabled: open,
  });

  // Fetch all participants for the organisation
  const { data: participants = [] } = useQuery({
    queryKey: [`/api/participants?organisationId=${jamNight?.organisationId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/participants?organisationId=${jamNight?.organisationId}`);
      return response.json();
    },
    enabled: open && !!jamNight?.organisationId,
  });

  // Fetch existing performers for this event to check for duplicates
  const { data: existingPerformers = [] } = useQuery({
    queryKey: [`/api/performers?jamNightId=${jamNightId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/performers?jamNightId=${jamNightId}`);
      return response.json();
    },
    enabled: open,
  });

  // Get set of participant IDs that are already added to this event
  const existingParticipantIds = new Set(
    existingPerformers
      .filter((p: any) => p.participantId && !p.removed)
      .map((p: any) => p.participantId)
  );

  // Filter participants based on search term
  const filteredParticipants = (() => {
    if (!participants) return [];
    
    if (searchTerm.trim().length === 0) {
      return participants.sort((a: Participant, b: Participant) => a.name.localeCompare(b.name));
    }
    
    return participants
      .filter((participant: Participant) => 
        participant.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a: Participant, b: Participant) => a.name.localeCompare(b.name));
  })();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      participants: [{ participantId: undefined, name: "" }],
      notes: "",
      priority: false,
      jamNightId,
    },
  });

  const createPerformersMutation = useMutation({
    mutationFn: async (formData: z.infer<typeof formSchema>) => {
      const promises = formData.participants.map(async (participant) => {
        const performerData = {
          participantId: participant.participantId,
          name: participant.name,
          notes: formData.notes,
          priority: formData.priority,
          jamNightId: formData.jamNightId,
        };
        console.log("Submitting performer data:", performerData);
        const response = await apiRequest("POST", "/api/performers", performerData);
        return response.json();
      });
      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/performers?jamNightId=${jamNightId}`] });
      // Also invalidate participants query to update autocomplete
      if (jamNight?.organisationId) {
        queryClient.invalidateQueries({ queryKey: [`/api/participants?organisationId=${jamNight.organisationId}`] });
      }
      form.reset();
      onOpenChange(false);
      toast({
        title: "Success",
        description: `Added ${form.getValues('participants').length} participant(s) to the event`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to add participant(s)",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    createPerformersMutation.mutate(data);
  };

  const handleParticipantSelect = (participant: Participant, index: number) => {
    const currentParticipants = form.getValues('participants');
    currentParticipants[index] = {
      participantId: participant.id,
      name: participant.name,
    };
    form.setValue('participants', currentParticipants);
    setSearchTerm("");
    setShowDropdown(false);
    setActiveInputIndex(-1);
  };

  const handleSearchChange = (value: string, index: number) => {
    setSearchTerm(value);
    setActiveInputIndex(index);
    setShowDropdown(true);
    
    const currentParticipants = form.getValues('participants');
    currentParticipants[index] = {
      participantId: undefined,
      name: value,
    };
    form.setValue('participants', currentParticipants);
  };

  const addParticipant = () => {
    const currentParticipants = form.getValues('participants');
    form.setValue('participants', [...currentParticipants, { participantId: undefined, name: "" }]);
  };

  const removeParticipant = (index: number) => {
    const currentParticipants = form.getValues('participants');
    if (currentParticipants.length > 1) {
      currentParticipants.splice(index, 1);
      form.setValue('participants', currentParticipants);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowDropdown(false);
      setActiveInputIndex(-1);
    };
    
    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setShowDropdown(false);
      setActiveInputIndex(-1);
      form.reset({
        participants: [{ participantId: undefined, name: "" }],
        notes: "",
        priority: false,
        jamNightId,
      });
    }
  }, [open, form, jamNightId]);

  const participantsFields = form.watch('participants');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Participants</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-3">
              <FormLabel>Participant Names</FormLabel>
              {participantsFields.map((field, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <div className="flex-1 relative">
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        value={index === activeInputIndex ? searchTerm : field.name}
                        onChange={(e) => handleSearchChange(e.target.value, index)}
                        onFocus={() => {
                          setActiveInputIndex(index);
                          setSearchTerm(field.name);
                          setShowDropdown(true);
                        }}
                        onClick={() => {
                          setActiveInputIndex(index);
                          setSearchTerm(field.name);
                          setShowDropdown(true);
                        }}
                        placeholder="Search for participant or enter new name..."
                        className="pl-10 pr-10"
                      />
                      {index === activeInputIndex && searchTerm && (
                        <button
                          type="button"
                          onClick={() => {
                            setSearchTerm("");
                            const currentParticipants = form.getValues('participants');
                            currentParticipants[index] = { participantId: undefined, name: "" };
                            form.setValue('participants', currentParticipants);
                          }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {/* Dropdown Results */}
                    {showDropdown && activeInputIndex === index && filteredParticipants.length > 0 && (
                      <div 
                        className={`absolute z-50 w-full mt-1 rounded-xl shadow-2xl max-h-60 overflow-auto border ${
                          isChrome || isMobileSafari
                            ? 'bg-white border-gray-200' 
                            : 'bg-white/20 backdrop-blur-xl border-white/30'
                        }`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          backgroundColor: (isChrome || isMobileSafari) ? 'white' : undefined,
                          backdropFilter: (isChrome || isMobileSafari) ? 'none' : undefined
                        }}
                      >
                        {filteredParticipants.map((participant: Participant) => {
                          const isAlreadyAdded = existingParticipantIds.has(participant.id);
                          const contactInfo = [participant.email, participant.phone].filter(Boolean).join(" / ");
                          return (
                            <div
                              key={participant.id}
                              onClick={() => {
                                if (!isAlreadyAdded) {
                                  handleParticipantSelect(participant, index);
                                }
                              }}
                              className={cn(
                                "px-4 py-3 border-b border-white/30 last:border-b-0",
                                isAlreadyAdded 
                                  ? "opacity-50 cursor-not-allowed bg-white/20" 
                                  : "hover:bg-white/40 cursor-pointer"
                              )}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="font-medium text-slate-900 flex items-center gap-2">
                                    {participant.name}
                                    {isAlreadyAdded && (
                                      <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                                        Already added
                                      </Badge>
                                    )}
                                  </div>
                                  {contactInfo && (
                                    <div className="text-sm text-slate-500">
                                      {contactInfo}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  {participantsFields.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeParticipant(index)}
                      className="mt-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addParticipant}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Participant
              </Button>
            </div>

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Priority Participants</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Mark these participants as priority
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Add any notes about these participants..."
                      className="min-h-[80px] resize-none"
                      rows={3}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30 shadow-lg transition-all duration-300"
                style={{
                  boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
                disabled={createPerformersMutation.isPending}
              >
                {createPerformersMutation.isPending ? "Adding..." : `Add ${participantsFields.length} Participant${participantsFields.length > 1 ? 's' : ''}`}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}